import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { adminApi, setToken, formatApiError } from "@/lib/admin";
import { CATEGORY_LABELS } from "@/lib/site";

const inputCls = "w-full border border-line bg-white px-4 py-3 text-sm text-ink outline-none placeholder:text-neutral-600 focus:border-regal-amber transition-colors";

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="mb-1.5 block font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-neutral-600">{hint}</span>}
  </label>
);

const EMPTY = {
  name: "", brand: "", category: "alientech", slug: "", sku: "",
  price_ex_vat: "", vat_rate: 0.2, sale_price: "", blurb: "", description: "",
  specs: [], includes: [], images: [], stock: "in_stock", badge: "",
  featured: false, visible: true, seo_title: "", seo_description: "",
  currency: "GBP", requires_master_details: false,
};

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export default function AdminEdit() {
  const { id } = useParams();
  const isNew = !id;
  const [form, setForm] = useState(EMPTY);
  const [specsText, setSpecsText] = useState("");
  const [includesText, setIncludesText] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const nav = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const load = useCallback(() => {
    if (isNew) return;
    adminApi().get("/admin/products")
      .then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (!p) { toast.error("Product not found"); nav("/admin"); return; }
        setForm({
          name: p.name || "", brand: p.brand || "", category: p.category || "alientech",
          slug: p.slug || "", sku: p.sku || "", price_ex_vat: p.price_ex_vat ?? "",
          vat_rate: p.vat_rate ?? 0.2, sale_price: p.sale_price ?? "", blurb: p.blurb || "",
          description: p.description || "", images: p.images || (p.image ? [p.image] : []),
          stock: p.stock || "in_stock", badge: p.badge || "", featured: !!p.featured,
          visible: p.visible !== false, seo_title: p.seo_title || "", seo_description: p.seo_description || "", currency: p.currency || "GBP", requires_master_details: !!p.requires_master_details,
        });
        setSpecsText(Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
        setIncludesText((p.includes || []).join("\n"));
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.status === 401) { setToken(null); nav("/admin/login"); }
        else toast.error("Failed to load product");
      });
  }, [id, isNew, nav]);

  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await adminApi().post("/admin/upload", fd);
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const specs = {};
    specsText.split("\n").forEach((line) => {
      const idx = line.indexOf(":");
      if (idx > 0) specs[line.slice(0, idx).trim()] = line.slice(idx + 1).trim();
    });
    const payload = {
      ...form,
      slug: form.slug ? slugify(form.slug) : slugify(form.name),
      price_ex_vat: parseFloat(form.price_ex_vat) || 0,
      vat_rate: parseFloat(form.vat_rate) || 0,
      sale_price: form.sale_price === "" ? null : parseFloat(form.sale_price),
      badge: form.badge || null,
      specs,
      includes: includesText.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) await adminApi().post("/admin/products", payload);
      else await adminApi().put(`/admin/products/${id}`, payload);
      toast.success(isNew ? "Product created" : "Product updated", { description: form.name });
      nav("/admin");
    } catch (err) {
      if (err.response?.status === 401) { setToken(null); nav("/admin/login"); }
      else toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <main className="min-h-screen bg-surface pt-28"><div className="container-regal"><div className="h-96 animate-pulse border border-line bg-white" /></div></main>;

  return (
    <main className="min-h-screen bg-surface pt-28 pb-20" data-testid="admin-edit-page">
      <div className="container-regal max-w-4xl">
        <Link to="/admin" className="mb-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-regal-amber" data-testid="admin-back-link"><ArrowLeft size={15} /> All products</Link>
        <h1 className="mb-8 font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight text-ink">{isNew ? "Add Product" : `Edit: ${form.name}`}</h1>

        <form onSubmit={save} className="space-y-8" data-testid="admin-product-form">
          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Core Details</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Product name *"><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="admin-field-name" /></Field>
              <Field label="Brand *"><input required value={form.brand} onChange={set("brand")} className={inputCls} data-testid="admin-field-brand" /></Field>
              <Field label="Category *">
                <select value={form.category} onChange={set("category")} className={inputCls} data-testid="admin-field-category">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="SKU"><input value={form.sku} onChange={set("sku")} className={inputCls} data-testid="admin-field-sku" /></Field>
              <Field label="URL slug" hint="Leave blank to auto-generate from the name"><input value={form.slug} onChange={set("slug")} placeholder={slugify(form.name || "product-name")} className={inputCls} data-testid="admin-field-slug" /></Field>
              <Field label="Badge">
                <select value={form.badge} onChange={set("badge")} className={inputCls} data-testid="admin-field-badge">
                  <option value="">None</option><option>MASTER</option><option>SLAVE</option>
                </select>
              </Field>
            </div>
          </section>

          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Pricing & Stock</p>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Price (ex VAT) *" hint="Set 0 for POA / enquiry-only"><input required type="number" step="0.01" min="0" value={form.price_ex_vat} onChange={set("price_ex_vat")} className={inputCls} data-testid="admin-field-price" /></Field>
              <Field label="Sale price (ex VAT)" hint="Optional — shown with original struck through"><input type="number" step="0.01" min="0" value={form.sale_price} onChange={set("sale_price")} className={inputCls} data-testid="admin-field-sale-price" /></Field>
              <Field label="VAT rate" hint="0.20 = 20%, 0 = no VAT">
                <select value={form.vat_rate} onChange={set("vat_rate")} className={inputCls} data-testid="admin-field-vat">
                  <option value={0.2}>20% (standard UK VAT)</option>
                  <option value={0}>0% (VAT exempt)</option>
                </select>
              </Field>
              <Field label="Stock status">
                <select value={form.stock} onChange={set("stock")} className={inputCls} data-testid="admin-field-stock">
                  <option value="in_stock">In stock</option><option value="low">Low stock</option><option value="out">Out of stock</option>
                </select>
              </Field>
              <Field label="Currency">
                <select value={form.currency} onChange={set("currency")} className={inputCls} data-testid="admin-field-currency">
                  <option value="GBP">GBP (£)</option><option value="EUR">EUR (€)</option>
                </select>
              </Field>
            </div>
            <div className="mt-6 flex flex-wrap gap-8">
              <label className="flex cursor-pointer items-center gap-3">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm((f) => ({ ...f, featured: e.target.checked }))} className="h-5 w-5 accent-[#0A3D8F]" data-testid="admin-field-featured" />
                <span className="text-sm font-medium text-ink">Featured product (homepage)</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm((f) => ({ ...f, visible: e.target.checked }))} className="h-5 w-5 accent-[#0A3D8F]" data-testid="admin-field-visible" />
                <span className="text-sm font-medium text-ink">Visible on website</span>
              </label>
              <label className="flex cursor-pointer items-center gap-3">
                <input type="checkbox" checked={form.requires_master_details} onChange={(e) => setForm((f) => ({ ...f, requires_master_details: e.target.checked }))} className="h-5 w-5 accent-[#0A3D8F]" data-testid="admin-field-requires-master" />
                <span className="text-sm font-medium text-ink">Requires Master tool details at purchase</span>
              </label>
            </div>
          </section>

          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Images</p>
            <div className="mb-4 flex flex-wrap gap-3">
              {form.images.map((src, i) => (
                <div key={i} className="relative border border-line" data-testid={`admin-image-${i}`}>
                  <img src={src} alt="" className="h-24 w-24 object-cover" />
                  {i === 0 && <span className="absolute bottom-0 left-0 bg-regal-amber px-1.5 py-0.5 font-mono text-[9px] uppercase text-white">Main</span>}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, j) => j !== i) }))} aria-label="Remove image" data-testid={`admin-image-remove-${i}`} className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-regal-red text-white"><X size={12} /></button>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <label className="inline-flex cursor-pointer items-center gap-2 border border-dashed border-linebright px-5 py-3 text-sm text-neutral-500 hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="admin-image-upload-label">
                <Upload size={15} /> Upload image
                <input type="file" accept="image/*" onChange={upload} className="hidden" data-testid="admin-image-upload" />
              </label>
              <div className="flex flex-1 min-w-[240px] gap-2">
                <input value={imgUrl} onChange={(e) => setImgUrl(e.target.value)} placeholder="…or paste an image URL" className={inputCls} data-testid="admin-image-url-input" />
                <button type="button" onClick={() => { if (imgUrl.trim()) { setForm((f) => ({ ...f, images: [...f.images, imgUrl.trim()] })); setImgUrl(""); } }} className="grid w-11 shrink-0 place-items-center border border-linebright text-neutral-500 hover:border-regal-amber hover:text-regal-amber" data-testid="admin-image-url-add"><Plus size={16} /></button>
              </div>
            </div>
          </section>

          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">Content</p>
            <div className="space-y-5">
              <Field label="Short blurb"><input value={form.blurb} onChange={set("blurb")} className={inputCls} data-testid="admin-field-blurb" /></Field>
              <Field label="Full description"><textarea rows={4} value={form.description} onChange={set("description")} className={inputCls} data-testid="admin-field-description" /></Field>
              <Field label="Specifications" hint="One per line: Name: Value"><textarea rows={5} value={specsText} onChange={(e) => setSpecsText(e.target.value)} placeholder={"Connection: OBD / Bench / Boot\nWarranty: 12 months"} className={inputCls} data-testid="admin-field-specs" /></Field>
              <Field label="What's included" hint="One item per line"><textarea rows={4} value={includesText} onChange={(e) => setIncludesText(e.target.value)} className={inputCls} data-testid="admin-field-includes" /></Field>
            </div>
          </section>

          <section className="border border-line bg-white p-6 sm:p-8">
            <p className="overline-label mb-6">SEO</p>
            <div className="space-y-5">
              <Field label="SEO title"><input value={form.seo_title} onChange={set("seo_title")} className={inputCls} data-testid="admin-field-seo-title" /></Field>
              <Field label="SEO description"><textarea rows={2} value={form.seo_description} onChange={set("seo_description")} className={inputCls} data-testid="admin-field-seo-description" /></Field>
            </div>
          </section>

          <div className="flex gap-4">
            <button type="submit" disabled={busy} data-testid="admin-save-button" className="bg-regal-amber px-12 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-white hover:bg-amber-400 transition-colors disabled:opacity-60">
              {busy ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
            </button>
            <Link to="/admin" className="border border-linebright px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.16em] text-ink hover:border-regal-amber hover:text-regal-amber transition-colors" data-testid="admin-cancel-button">Cancel</Link>
          </div>
        </form>
      </div>
    </main>
  );
}
